import React, { useState } from 'react';
import { NavLink, useNavigate, Link } from 'react-router-dom';
import { LayoutDashboard, FolderKanban, Compass, FileText, UserCircle, Headset, LogOut, Menu, X, ShieldCheck, ArrowLeftRight, Wallet } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useRecoveryPortal } from '../../context/RecoveryPortalContext';
import SupportChat from '../SupportChat';

const NAV = [
    { to: '/recovery/dashboard', icon: LayoutDashboard, label: 'Dashboard', key: 'tracker' },
    { to: '/recovery/cases', icon: FolderKanban, label: 'My Cases', key: 'tracker' },
    { to: '/recovery/deposit', icon: Wallet, label: 'Deposit', key: 'deposit' },
    { to: '/recovery/methods', icon: Compass, label: 'Recovery Methods', key: 'methods' },
    { to: '/recovery/documents', icon: FileText, label: 'Documents', key: 'documents' },
    { to: '/recovery/profile', icon: UserCircle, label: 'Profile', key: null },
    { to: '/recovery/support', icon: Headset, label: 'Support', key: 'support' },
];

const SidebarContent = ({ onNavigate, idSuffix = '' }) => {
    const { user, logout } = useAuth();
    const { config } = useRecoveryPortal();
    const navigate = useNavigate();
    const sections = config.sections || {};

    const handleLogout = () => { logout(); navigate('/recovery/login'); };

    return (
        <div className="flex flex-col h-full">
            <div className="p-5 border-b border-white/5">
                <div className="flex items-center gap-3">
                    <img src={config.logo_url || '/logo.jpg'} alt={config.portal_name} className="w-10 h-10 rounded-full border-2 border-[var(--rp-accent)]" />
                    <div className="min-w-0">
                        <p className="font-rajdhani font-bold uppercase tracking-wider text-sm text-[var(--rp-accent)] truncate">{config.portal_name}</p>
                        <p className="text-[9px] uppercase tracking-[0.3em] text-gray-500">Recovery Wallet</p>
                    </div>
                </div>
            </div>
            <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
                {NAV.filter((n) => !n.key || sections[n.key] !== false).map((n) => (
                    <NavLink key={n.to} to={n.to} onClick={onNavigate} data-testid={`rnav-${n.label.toLowerCase().replace(/ /g, '-')}${idSuffix}`}
                        className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all ${isActive ? 'bg-[var(--rp-accent)]/15 text-[var(--rp-accent)] font-semibold' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}>
                        <n.icon size={17} strokeWidth={1.6} /> {n.label}
                    </NavLink>
                ))}
                {user?.is_admin && (
                    <>
                        <Link to="/console" onClick={onNavigate} className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-purple-300 hover:bg-purple-500/10" data-testid={`rnav-admin-console${idSuffix}`}><ShieldCheck size={17} strokeWidth={1.6} /> Admin Console</Link>
                        <Link to="/dashboard" onClick={onNavigate} className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-[#5BA4A4] hover:bg-[#5BA4A4]/10" data-testid={`rnav-trading-portal${idSuffix}`}><ArrowLeftRight size={17} strokeWidth={1.6} /> Trading Portal</Link>
                    </>
                )}
            </nav>
            <div className="p-4 border-t border-white/5">
                <div className="flex items-center gap-3 mb-3">
                    <div className="w-9 h-9 rounded-full bg-[var(--rp-accent)]/20 flex items-center justify-center text-[var(--rp-accent)] font-bold">{(user?.name || 'U')[0].toUpperCase()}</div>
                    <div className="min-w-0">
                        <p className="text-sm text-white truncate">{user?.name}</p>
                        <p className="text-[10px] text-gray-500 truncate font-mono">{user?.email}</p>
                    </div>
                </div>
                <button onClick={handleLogout} className="w-full flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-wider text-red-400 hover:bg-red-500/10 rounded-lg" data-testid={`recovery-logout-btn${idSuffix}`}><LogOut size={14} /> Logout</button>
            </div>
        </div>
    );
};

const RecoveryLayout = ({ children }) => {
    const [open, setOpen] = useState(false);
    const { config, themeStyle } = useRecoveryPortal();

    return (
        <div className="min-h-screen bg-[#07090d] text-white flex" style={themeStyle} data-testid="recovery-layout">
            <div className="fixed inset-0 pointer-events-none opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(var(--rp-accent) 1px, transparent 1px)', backgroundSize: '28px 28px' }} />
            <aside className="hidden lg:flex w-64 shrink-0 border-r border-white/5 bg-[#0a0d13] flex-col sticky top-0 h-screen z-20">
                <SidebarContent />
            </aside>

            {/* Mobile header */}
            <div className="lg:hidden fixed top-0 inset-x-0 z-30 flex items-center justify-between px-4 h-14 bg-[#0a0d13]/95 backdrop-blur border-b border-white/5">
                <div className="flex items-center gap-2">
                    <img src={config.logo_url || '/logo.jpg'} alt="" className="w-8 h-8 rounded-full border border-[var(--rp-accent)]" />
                    <span className="font-rajdhani font-bold uppercase tracking-wider text-sm text-[var(--rp-accent)]">{config.portal_name}</span>
                </div>
                <button onClick={() => setOpen(!open)} className="p-2 text-gray-300" data-testid="recovery-mobile-menu">{open ? <X size={22} /> : <Menu size={22} />}</button>
            </div>
            {open && (
                <div className="lg:hidden fixed inset-0 z-40">
                    <div className="absolute inset-0 bg-black/70" onClick={() => setOpen(false)} />
                    <aside className="absolute left-0 top-0 h-full w-72 bg-[#0a0d13] border-r border-white/5"><SidebarContent onNavigate={() => setOpen(false)} idSuffix="-mobile" /></aside>
                </div>
            )}

            <main className="flex-1 min-w-0 relative z-10 pt-14 lg:pt-0">
                {children}
            </main>
            <SupportChat />
        </div>
    );
};

export default RecoveryLayout;
